import moment from 'moment';
import {
  API_DATE_TIME_FORMAT,
  API_TIME_FORMAT,
  API_DATE_TIME_FORMAT_ZERO_SECONDS,
  API_DATE_FORMAT,
  ORDER_STATUSES,
  ACCOUNT_BILLING_STATUS_MAP,
  WARNINGS_AND_INFOS_TYPES
} from '@ubeya/shared/constants';
import { getTimeRangeWithDates } from '@ubeya/shared/utils/time';
import { CREATE_NEW_MAIN_PROJECT } from '@ubeya/shared/hooks/account/useMainProjects';
import { flatten, isEmpty } from 'lodash';
import {
  FULLY_BOOKED_BOOKING_STATUS,
  LIVE_VIEW_FILTER_STATUSES,
  NON_BOOKED_BOOKING_STATUS,
  OVER_77_WARNING,
  PARTIALLY_BOOKED_BOOKING_STATUS,
  PAYROLL_PROCESS_STATUS,
  PROJECT_SCHEDULING_FILTERS_SLUGS,
  UNDER_18_WARNING
} from '@ubeya/shared-web/constants';
import { SORT_BY_STRING, SORT_STRING } from '@ubeya/shared/utils/sorting';
import { isShiftOnBudgetBoard } from '@ubeya/shared/utils/dailyBudget';
import {
  ONB_ACCOUNT_ID,
  COMPASS_DE_ORGANIZATION_ID,
  ONDI_ACCOUNT_ID,
  DELAWARE_NORTH_ID
} from '@ubeya/shared/utils/accounts';
import { INACTIVE_ORDER_TYPES, MILLISECONDS_IN_A_DAY } from './constants';
import {
  LIST_TYPE_ENUM,
  GROUP_BY_OPTIONS,
  TIME_RANGE_OPTIONS,
  MONTHLY_VIEW_OPTIONS_MAPPED,
  TIME_RANGE_KEY_OPTIONS,
  POSITIONS_OPTIONS,
  AREAS_OPTIONS,
  SLOT_WARNING_TYPES,
  PROJECT_GROUP_BY_OPTIONS,
  COST_CENTER_OPTIONS,
  EMPLOYEE_WORKING_HOURS_PERIOD_OPTIONS_MAPPED,
  PROJECT_INFO_EMPLOYEE_WORKING_HOURS_TYPE,
  BOOKED_ON_SAME_DAY_WARNING_HIERARCHY,
  CONFIRMED_BY_TYPE
} from './constants';
import { getAttendanceFlags, getAttendanceNow } from './liveAttendance';
import { getShiftPlaceIds, isMatchingLocationFilters, migrateLegacyLocationsFilter } from './schedulingBoardFilters';
import { getLatestBreak } from 'liveView/utils';

const MAIN_PROJECT_DATE_FILTER_BUFFER_WEEKS = 1;

export const changePeriod = (timeRangeKey, units = null, amount) => {
  const selected = TIME_RANGE_OPTIONS.find(({ value }) => value === timeRangeKey);

  const selectedAmount = (amount || selected.amount || 1) - 1;
  const selectedUnits = units || selected.units;

  return {
    key: timeRangeKey,
    startDate: moment().startOf(selectedUnits).startOf('day').toISOString(),
    endDate: moment().add(selectedAmount, selectedUnits).endOf(selectedUnits).startOf('day').toISOString()
  };
};

export const nextPeriod = (timeRange, units = null) => {
  const selected = TIME_RANGE_OPTIONS.find(({ value }) => value === timeRange.key);
  return {
    key: timeRange.key,
    startDate: moment(timeRange.startDate)
      .add(selected.amount, units || selected.units)
      .startOf(units || selected.units)
      .toISOString(),
    endDate: moment(timeRange.endDate)
      .add(selected.amount, units || selected.units)
      .endOf(units || selected.units)
      .toISOString()
  };
};

export const prevPeriod = (timeRange, units = null) => {
  const selected = TIME_RANGE_OPTIONS.find(({ value }) => value === timeRange.key);
  return {
    key: timeRange.key,
    startDate: moment(timeRange.startDate)
      .add(-selected.amount, units || selected.units)
      .startOf(units || selected.units)
      .toISOString(),
    endDate: moment(timeRange.endDate)
      .add(-selected.amount, units || selected.units)
      .endOf(units || selected.units)
      .toISOString()
  };
};

/** Same path as any scheduling project deep link — always the client child project, not the supplier mirror. */
export const getMainProjectChildSchedulingPath = ({ accountId, project }) => {
  if (accountId == null || !project?.id || project.branchId == null) {
    return null;
  }

  return `/${accountId}/scheduling/branch/${project.branchId}/project/${project.id}`;
};

export const getMainProjectDateFilterRange = ({ dateRange, showMultiday }) => {
  if (!dateRange?.startDate) return null;

  const startDate = moment(dateRange.startDate);
  if (!startDate.isValid()) return null;

  const endDate = showMultiday ? moment(dateRange?.endDate) : startDate;
  if (!endDate.isValid()) return null;

  return {
    startDate: startDate.clone().subtract(MAIN_PROJECT_DATE_FILTER_BUFFER_WEEKS, 'week'),
    endDate: endDate.clone().add(MAIN_PROJECT_DATE_FILTER_BUFFER_WEEKS, 'week')
  };
};

export const isMainProjectInDateFilterRange = ({ mainProject, dateFilterRange }) => {
  if (!dateFilterRange) return true;
  if (!mainProject.startDate) return true;

  const mainProjectStartDate = moment(mainProject.startDate);
  if (!mainProjectStartDate.isValid()) return false;

  const mainProjectEndDate = mainProject.endDate ? moment(mainProject.endDate) : mainProjectStartDate;
  if (!mainProjectEndDate.isValid()) return false;

  return (
    mainProjectStartDate.isSameOrBefore(dateFilterRange.endDate, 'day') &&
    mainProjectEndDate.isSameOrAfter(dateFilterRange.startDate, 'day')
  );
};

export const filterMainProjectOptions = ({
  mainProjectOptions,
  dateFilterRange,
  currentValue,
  canManageMainProjects,
  canAssignMainEvent,
  disabledTooltip
}) =>
  (mainProjectOptions || [])
    .filter((mainProject) => {
      if (mainProject.value === CREATE_NEW_MAIN_PROJECT) return canManageMainProjects;
      if (mainProject.value === currentValue) return true;

      return isMainProjectInDateFilterRange({ mainProject, dateFilterRange });
    })
    // An admin with MANAGE_MAIN_PROJECTS but not ASSIGN_EVENTS_TO_MAIN_PROJECT can still create a
    // new main event, but can't assign to an existing one — keep those options visible for
    // context, just disabled, rather than hiding them.
    .map((mainProject) =>
      mainProject.value !== CREATE_NEW_MAIN_PROJECT && !canAssignMainEvent
        ? { ...mainProject, isDisabled: true, tooltip: disabledTooltip }
        : mainProject
    );

export const minMaxShiftsTime = (shifts, timeFormat) => {
  const minTime = moment.min(shifts.map(({ startTime }) => moment(startTime, API_DATE_TIME_FORMAT))).format(timeFormat);

  if (shifts.some(({ endTime }) => endTime)) {
    const maxTime = moment
      .max(shifts.filter(({ endTime }) => endTime).map(({ endTime }) => moment(endTime, API_DATE_TIME_FORMAT)))
      .format(timeFormat);
    return { minTime, maxTime };
  }

  return { minTime, maxTime: null };
};

export const minMaxShiftsTimeWithStartTime = (shifts, date, timeFormat) => {
  const mappedShifts = shifts.map(({ startTime }) => {
    if (moment(startTime).isBefore(moment(date))) {
      return moment(`${date} 00:00:00`);
    }

    return moment(startTime, API_DATE_TIME_FORMAT);
  });

  const minTime = moment.min([...mappedShifts, ...mappedShifts]).format(timeFormat);

  const maxTimeWithStartTime = moment.max([
    ...shifts.map(({ startTime }) => {
      const momentDate = moment(date);
      const momentStartTime = moment(startTime);

      const start = momentStartTime.isValid()
        ? momentDate.set({
            hour: momentStartTime.get('hour'),
            minute: momentStartTime.get('minute'),
            seconds: 0
          })
        : null;

      return start;
    }),
    ...shifts.map(({ endTime }) => {
      const momentDate = moment(date);
      const momentStartTime = moment(endTime);

      const end = momentStartTime.isValid()
        ? momentDate.set({
            hour: momentStartTime.get('hour'),
            minute: momentStartTime.get('minute'),
            seconds: 0
          })
        : null;

      return end;
    }),
    moment(`${date} 23:59:59`)
  ]);

  if (shifts.some(({ endTime }) => endTime)) {
    const maxTime = moment
      .max([
        ...shifts
          .filter(({ endTime }) => endTime)
          .map(({ endTime }) => {
            const momentDate = moment(date);
            const momentEndTime = moment(endTime);

            const end = momentEndTime.isValid()
              ? momentDate.set({
                  hour: momentEndTime.get('hour'),
                  minute: momentEndTime.get('minute'),
                  seconds: 0
                })
              : null;

            return end;
          }),

        maxTimeWithStartTime
      ])
      .format(timeFormat);

    return { minTime, maxTime };
  }

  return { minTime, maxTime: null };
};

export const getDefaultTimeRange = (branches, selectedBranchId, inputUnits = null) => {
  const selectedBranch = branches.find(({ id }) => id === selectedBranchId);
  const { defaultScheduleView, defaultMonthlyView } = selectedBranch?.scheduleConfig || {};

  const selected = TIME_RANGE_OPTIONS.find(({ value }) => value === defaultScheduleView) || TIME_RANGE_OPTIONS[0];

  const useWeekUnitsInMonthKey =
    defaultScheduleView === TIME_RANGE_KEY_OPTIONS.MONTHLY &&
    [MONTHLY_VIEW_OPTIONS_MAPPED.WEEK_VIEW, MONTHLY_VIEW_OPTIONS_MAPPED.WEEK_LIST_VIEW].includes(defaultMonthlyView);

  const units = inputUnits || (useWeekUnitsInMonthKey ? 'week' : undefined);

  return {
    key: selected.value,
    startDate: moment()
      .startOf(units || selected.units)
      .startOf('day')
      .toISOString(),
    endDate: moment()
      .endOf(units || selected.units)
      .endOf('day')
      .toISOString()
  };
};

export const getDefaultWeeklyGroupBy = (branches, selectedBranchId) => {
  const selectedBranch = branches.find(({ id }) => id === selectedBranchId);
  const selectedDefaultWeeklyGroupByView =
    selectedBranch?.scheduleConfig?.defaultWeeklyGroupByView || GROUP_BY_OPTIONS.EMPLOYEE;

  return selectedDefaultWeeklyGroupByView;
};

export const getDefaultMonthlyView = (branches, selectedBranchId) => {
  const selectedBranch = branches.find(({ id }) => id === selectedBranchId);
  const selectedDefaultMonthlyView =
    selectedBranch?.scheduleConfig?.defaultMonthlyView || MONTHLY_VIEW_OPTIONS_MAPPED.MONTH_VIEW;

  return selectedDefaultMonthlyView;
};

const timeRangesIntersection = (start1, end1, start2, end2, fieldName = null, includeEdges = true) => {
  const prevStart = moment(start1, API_DATE_TIME_FORMAT_ZERO_SECONDS);
  const prevEnd = moment(end1, API_DATE_TIME_FORMAT_ZERO_SECONDS);
  const newStart = moment(start2, API_DATE_TIME_FORMAT_ZERO_SECONDS);
  const newEnd = moment(end2, API_DATE_TIME_FORMAT_ZERO_SECONDS);

  // isBetween is intentionally exclusive; edge inclusivity is handled by the two spanning
  // branches below (driven by includeEdges), not by these endpoint-inside checks.
  if (newStart.isBetween(prevStart, prevEnd, undefined, '()')) {
    return fieldName ? fieldName === 'start' : true;
  }

  if (newEnd.isBetween(prevStart, prevEnd, undefined, '()')) {
    return fieldName ? fieldName === 'end' : true;
  }

  if (includeEdges && newStart.isSameOrBefore(prevStart) && newEnd.isSameOrAfter(prevEnd)) {
    return true;
  }

  // The new range fully spans the prev range. `isSameOrBefore`/`isSameOrAfter` (rather than
  // strict `isBefore`/`isAfter`) catches the case where one boundary is shared — e.g. a shift
  // starting exactly at 00:00 over a 00:00–06:00 Not-Available block — which is still a real
  // positive-length overlap, not a mere edge touch. The non-degenerate guard keeps a
  // zero-length prev range from matching on contact alone.
  if (
    !includeEdges &&
    prevStart.isBefore(prevEnd) &&
    newStart.isSameOrBefore(prevStart) &&
    newEnd.isSameOrAfter(prevEnd)
  ) {
    return true;
  }

  return false;
};

export const checkOverlappingHours = ({ data, newStart, newEnd, fieldName, includeEdges = true }) =>
  data.some(
    ({ startTime, endTime }) => !!timeRangesIntersection(startTime, endTime, newStart, newEnd, fieldName, includeEdges)
  );

export const totalDuration = (startTime, endTime) => {
  if (!startTime || !endTime) {
    return moment.duration(0);
  }
  return moment.duration(moment(endTime, API_DATE_TIME_FORMAT).diff(moment(startTime, API_DATE_TIME_FORMAT)));
};

export const celsiusToFahrenheit = (degree) => Math.floor(degree * 1.8 + 32);

const formatTimeByHour = (date, hour, withSuffix = true) =>
  `${moment(date).format('YYYY-MM-DD')} ${hour}${withSuffix ? ':00:00' : ''}`;

/* 
    formatGeneralAvailability function:
        input - general availability - when the employee is available.
        output - general not availability - when the employee is not available, 
        with respect to account start and end hour of work day.
 */

export const formatGeneralAvailability = (data, date, accountStartHour, accountEndHour) => {
  const generalAvailabilityByNotAvailable = [];

  data.forEach((availability) => {
    if (availability?.type === 'Not Available') {
      generalAvailabilityByNotAvailable.push(availability);
      return;
    }

    const { start, end, type, ...rest } = availability;

    const accountStartTime = formatTimeByHour(date, accountStartHour);
    const accountEndTime = formatTimeByHour(date, accountEndHour);
    const availabilityStart = formatTimeByHour(
      date,
      moment(start, API_DATE_TIME_FORMAT).format(API_TIME_FORMAT),
      false
    );
    const endMoment = moment(end, API_DATE_TIME_FORMAT);
    const sameDayEnd = formatTimeByHour(date, endMoment.format(API_TIME_FORMAT), false);
    // If the end time placed on the same day falls at or before the availability start, it is a
    // cross-midnight end (e.g. 08:00–00:05 or 08:00–00:00). Shift it to the next day so Range 2
    // ("after availability") starts at the correct datetime and doesn't cover the whole same day.
    const availabilityEnd = !moment(sameDayEnd).isAfter(availabilityStart)
      ? moment(date)
          .add(1, 'day')
          .startOf('day')
          .add(endMoment.hours(), 'hours')
          .add(endMoment.minutes(), 'minutes')
          .add(endMoment.seconds(), 'seconds')
          .format(API_DATE_TIME_FORMAT)
      : sameDayEnd;

    const nextDayEnd = moment(date).add(1, 'day').endOf('day').format(API_DATE_TIME_FORMAT);

    // Range 1: before availability start — clipped to account working hours
    const beforeRange = { start: moment(date).startOf('day').format(API_DATE_TIME_FORMAT), end: availabilityStart };
    if (timeRangesIntersection(accountStartTime, accountEndTime, beforeRange.start, beforeRange.end)) {
      const notAvailabilityStart = moment(beforeRange.start).isBefore(accountStartTime)
        ? accountStartTime
        : beforeRange.start;
      const notAvailabilityEnd = moment(beforeRange.end).isAfter(accountEndTime) ? accountEndTime : beforeRange.end;
      generalAvailabilityByNotAvailable.push({
        ...rest,
        type,
        startTime: notAvailabilityStart,
        endTime: notAvailabilityEnd
      });
    }

    // Range 2: after availability end — extends to next-day end to catch cross-midnight shifts.
    // Not clipped to accountEndTime: shifts can extend past account default hours.
    if (moment(availabilityEnd).isBefore(nextDayEnd)) {
      const notAvailabilityStart = moment(availabilityEnd).isBefore(accountStartTime)
        ? accountStartTime
        : availabilityEnd;
      generalAvailabilityByNotAvailable.push({ ...rest, type, startTime: notAvailabilityStart, endTime: nextDayEnd });
    }
  });

  return generalAvailabilityByNotAvailable;
};

const DAYS_OF_WEEK = [0, 1, 2, 3, 4, 5, 6];

export const fillMissingGeneralAvailabilityDays = ({
  generalAvailabilities,
  employeeIds,
  unavailableByDefault,
  isLoading
}) => {
  if (!unavailableByDefault || isLoading) {
    return generalAvailabilities;
  }

  const byEmployeeId = generalAvailabilities.reduce((output, entry) => {
    if (!output[entry.employeeId]) {
      output[entry.employeeId] = [];
    }
    output[entry.employeeId].push(entry);
    return output;
  }, {});

  const missingDayEntries = employeeIds.flatMap((employeeId) => {
    const dayEntries = byEmployeeId[employeeId] || [];
    return DAYS_OF_WEEK.filter((day) => !dayEntries.some((entry) => entry.day === day)).map((day) => ({
      day,
      type: 'Not Available',
      employeeId
    }));
  });

  return [...generalAvailabilities, ...missingDayEntries];
};

/*
    isBookedOutsideAvailability function:
        Decides whether a single shift conflicts with an employee's partial (time-ranged)
        general availability. Shared by HourlyEmployees (bookedOutsideAvailabilityByEmployee)
        and WeeklyHourlyEmployees (bookedOutsideAvailability) so the cross-midnight logic
        lives in one place.

        Callers handle the whole-day "Not Available" / time-off / specific-week availability
        cases and the result accumulation themselves; this function only covers the partial
        general-availability decision.

        getNextDayAvailabilities(nextDayOfWeek) — caller-supplied resolver returning the raw
        general-availability entries for the next day-of-week (each component stores them in a
        different shape), used to evaluate the cross-midnight portion of a shift.

        date must be a day string in API_DATE_FORMAT (YYYY-MM-DD) — it is compared against the
        shift's end date to detect a cross-midnight shift.

        Returns true → the shift falls outside availability (show warning).
 */
export const isBookedOutsideAvailability = ({
  newStart,
  newEnd,
  date,
  generalAvailabilitiesFormatted,
  getNextDayAvailabilities,
  startOfDaysHours,
  endOfDaysHours
}) => {
  // No overlap with any not-available range → the shift is fully inside availability.
  if (!checkOverlappingHours({ data: generalAvailabilitiesFormatted, newStart, newEnd, includeEdges: false })) {
    return false;
  }

  // Same-day shift: the full-shift overlap above settles it.
  const endDate = newEnd.format(API_DATE_FORMAT);
  if (endDate === date) {
    return true;
  }

  // Cross-midnight shift: split at midnight. If the same-day portion already conflicts, done.
  const midnight = moment(endDate, API_DATE_FORMAT).startOf('day');
  if (
    checkOverlappingHours({ data: generalAvailabilitiesFormatted, newStart, newEnd: midnight, includeEdges: false })
  ) {
    return true;
  }

  // Same-day portion is clean — the conflict (if any) is in the next-day portion.
  const nextDayOfWeek = moment(endDate, API_DATE_FORMAT).day();
  const nextDayRawAvailabilities = getNextDayAvailabilities?.(nextDayOfWeek);

  // Mirror the same-day notAvailableWholeDay fast-path: if the next day is wholly Not Available,
  // any cross-midnight portion into it is a conflict. formatGeneralAvailability pushes
  // 'Not Available' entries as-is (no startTime/endTime), so checkOverlappingHours would
  // silently miss them.
  if (nextDayRawAvailabilities?.some(({ type }) => type === 'Not Available')) {
    return true;
  }

  const nextDayGeneralAvailabilitiesFormatted = formatGeneralAvailability(
    nextDayRawAvailabilities?.filter(({ type }) => type !== 'Available') || [],
    endDate,
    startOfDaysHours,
    endOfDaysHours
  );

  return checkOverlappingHours({
    data: nextDayGeneralAvailabilitiesFormatted,
    newStart: midnight,
    newEnd,
    includeEdges: false
  });
};

// sub-order shifts (deeper OFS levels) are mapped to a parent ORDER shift (an order shift, not an internal one).
// returns a Map of parent order shift id -> its sub-order shifts, so their bookings/employees can be
// attributed to the standalone parent order shift that actually renders.
export const groupSubOrderShiftsByParentOrderShiftId = (shifts = []) => {
  const orderShiftIds = new Set(shifts.filter(({ isOrder, orderId }) => isOrder || orderId).map(({ id }) => id));

  return shifts.reduce((map, shift) => {
    const parentId = shift.mappedToAnyShiftInCurrentProject?.[0];

    // group only shifts whose parent is itself an order shift (a sub-order of an order)
    if (!parentId || !orderShiftIds.has(parentId)) return map;
    if (!map.has(parentId)) {
      map.set(parentId, []);
    }
    map.get(parentId).push(shift);
    return map;
  }, new Map());
};

export const shiftsStats = ({ shifts = [], orders = [], isProjectStatsActualBooked = false }) => {
  const allShifts = shifts
    // extract clientShift to not include it inside shifts
    // as we want count clientShift only for orders
    // eslint-disable-next-line
    .map(({ clientShift, ...shift }) => shift)

    // do not take into account isOverbooking shifts stats
    .filter(({ isOverbooking }) => !isOverbooking)
    .concat(
      flatten(
        (orders || []).map(({ shifts: shiftsData }) =>
          shiftsData.filter(({ clientShift }) => !clientShift?.isDeleted).map((curr) => ({ ...curr, isOrder: true }))
        )
      )
    );

  const statsMap = {
    totalConfirmed: 0,
    totalClockedIn: 0,
    totalClockedOut: 0,
    totalBooked: 0,
    totalBookedStandBy: 0,
    totalExternalOrdered: 0,
    totalNeeded: 0,
    totalAvailable: 0
  };

  // internal + mapped external employees calculated here (from backend under mapped shift stats)
  allShifts.forEach(({ stats, needed, isOrder }) => {
    if (isOrder) return;

    statsMap.totalConfirmed += stats?.confirmed || 0;
    statsMap.totalClockedIn += stats?.clockedIn || 0;
    statsMap.totalClockedOut += stats?.clockedOut || 0;
    statsMap.totalBooked += stats?.booked || 0;
    statsMap.totalBookedStandBy += stats?.bookedStandBy || 0;
    statsMap.totalExternalOrdered += stats?.externalOrdered || 0;
    statsMap.totalAvailable += stats?.available || 0;
    statsMap.totalNeeded += needed || 0;
  });

  // the backend returns no stats for sub-order shifts and they are skipped in the loop below,
  // so their bookings must be attributed to the standalone parent order shift they belong to.
  const subOrderShiftsByParentId = groupSubOrderShiftsByParentOrderShiftId(allShifts);

  // stand alone shifts calculated here -stats not returned
  // the backend does not return stats for orders shift (needs to be calculated here)
  allShifts.forEach(({ id, slots = [], needed, isOrder, clientShift, mappedToAnyShiftInCurrentProject = [] }) => {
    if (!isOrder) return;

    //calculated in the shift that ist linked to
    if (mappedToAnyShiftInCurrentProject.length > 0) return;

    // include bookings from sub-order shifts mapped to this standalone parent order shift
    const subOrderSlots = (subOrderShiftsByParentId.get(id) || []).flatMap((s) => s.slots || []);
    const combinedSlots = [...slots, ...subOrderSlots];

    const calculatedNeeded = clientShift?.needed || needed || 0;
    const actualBooked = combinedSlots.length;

    const booked = isProjectStatsActualBooked ? actualBooked : Math.max(actualBooked, calculatedNeeded);

    const clockedIn = combinedSlots.filter(({ timesheet }) => !!timesheet?.startTime).length;
    const clockedOut = combinedSlots.filter(({ timesheet }) => !!timesheet?.endTime).length;

    //external is auto confirmed, use the actual booked
    statsMap.totalConfirmed += actualBooked || 0;
    statsMap.totalBooked += booked || 0;
    statsMap.totalClockedIn += clockedIn || 0;
    statsMap.totalClockedOut += clockedOut || 0;
    statsMap.totalNeeded += calculatedNeeded;
  });

  return statsMap;
};

export const getClockingValues = ({ clockIn: clockInTime, clockOut: clockOutTime, timesheet, date }) => {
  if (!timesheet) {
    return undefined;
  }

  const { startTime: clockIn, endTime: clockOut } = getTimeRangeWithDates({
    startTime: clockInTime,
    endTime: clockOutTime,
    date
  });

  const updateClockIn =
    moment(clockIn, API_DATE_TIME_FORMAT).diff(moment(timesheet?.startTime, API_DATE_TIME_FORMAT), 'minute') !== 0;

  const updateClockOut =
    moment(clockOut, API_DATE_TIME_FORMAT).diff(moment(timesheet?.endTime, API_DATE_TIME_FORMAT), 'minute') !== 0;

  const clockingValues =
    updateClockIn || updateClockOut
      ? {
          ...(updateClockIn && { clockIn }),
          ...(updateClockOut && { clockOut })
        }
      : undefined;
  return clockingValues;
};

//availability
export const warningMetadataPartialPreferred = ({ metadata, timeFormat }) => {
  const { startTime, endTime } = metadata || {};

  if (!startTime || !endTime) {
    return '';
  }

  return `${moment(startTime, API_DATE_TIME_FORMAT).format(timeFormat)}${
    endTime ? `- ${moment(endTime, API_DATE_TIME_FORMAT).format(timeFormat)} ` : ''
  }`;
};

export const formatPreferenceTimeRange = ({ metadata, timeFormat }) => {
  const { startTime, endTime } = metadata || {};

  if (!startTime || !endTime) {
    return null;
  }

  return {
    startTime: moment(startTime, API_DATE_TIME_FORMAT).format(timeFormat),
    endTime: moment(endTime, API_DATE_TIME_FORMAT).format(timeFormat)
  };
};

export const warningMetadataFactory = ({ listType, metadata, timeFormat }) => {
  switch (listType) {
    case LIST_TYPE_ENUM.PARTIAL_PREFERRED:
      return warningMetadataPartialPreferred({ metadata, timeFormat });

    default:
      break;
  }
};

export const formatMinutesToString = (minutes, t) => {
  if (minutes < 60) {
    return t(`minutesBefore`, { minutes });
  }

  if (minutes < 60 * 24) {
    return t(`hoursBefore`, { minutes: +Number.parseFloat(minutes / 60).toFixed(2) });
  }

  return t(`daysBefore`, { minutes: +Number.parseFloat(minutes / 60 / 24).toFixed(2) });
};

export const generateUniqueWarnings = (warnings = []) => {
  if (warnings.length <= 0) {
    return null;
  }

  const warningTypes = new Set();

  // Filter out warnings that the card renders on its own — compliance, WTD, and the partial
  // preference window, which the card shows as a short list-type label and spells out in full
  // behind the explanation mark
  const filteredWarnings = (warnings || []).filter(
    ({ type }) =>
      ![
        SLOT_WARNING_TYPES.COMPLIANCE.type,
        SLOT_WARNING_TYPES.RELEASED_SAME_DAY.type,
        SLOT_WARNING_TYPES.WTD.type,
        SLOT_WARNING_TYPES.PARTIAL_PREFERRED.type
      ].includes(type)
  );

  // Find the highest priority warning from the hierarchy
  const hierarchyWarnings = filteredWarnings.filter(({ type }) => BOOKED_ON_SAME_DAY_WARNING_HIERARCHY.includes(type));

  const highestPriorityWarningType =
    hierarchyWarnings.length > 0
      ? hierarchyWarnings.reduce((highestType, { type: currentType }) => {
          const currentIndex = BOOKED_ON_SAME_DAY_WARNING_HIERARCHY.indexOf(currentType);
          const highestIndex = BOOKED_ON_SAME_DAY_WARNING_HIERARCHY.indexOf(highestType);

          return currentIndex < highestIndex ? currentType : highestType;
        }, hierarchyWarnings[0].type)
      : null;

  const flattenWarnings = filteredWarnings.reduce((all, warning) => {
    const { type } = warning;

    // Skip hierarchy warnings that are not the highest priority
    if (BOOKED_ON_SAME_DAY_WARNING_HIERARCHY.includes(type) && warning.type !== highestPriorityWarningType) {
      return all;
    }

    if (!warningTypes.has(type)) {
      warningTypes.add(type);
      all.push(warning);
    }

    return all;
  }, []);

  return flattenWarnings;
};

export const getFlattenWarningsSlugs = (user) => {
  const warnings = generateUniqueWarnings(user.warnings || []);

  return (warnings || []).map(({ slug }) => slug) || [];
};

export const getAllPositionsInProjects = (projects) =>
  (projects || [])
    .map(({ shifts }) => shifts.filter(({ positionId }) => !!positionId).map(({ positionId }) => positionId))
    .reduce((all, positions) => {
      positions.forEach((value) => all.add(value));
      return all;
    }, new Set());

export const getGroupedBookedEmployeesByPosition = ({ shifts, employeesMap }) => {
  const bookedEmployeesByPosition = {};

  (shifts || []).forEach(({ positionId, slots }) =>
    (slots || []).forEach(({ employeeId }) => {
      if (!employeeId) {
        return;
      }
      if (!bookedEmployeesByPosition[positionId]) {
        bookedEmployeesByPosition[positionId] = [];
      }

      bookedEmployeesByPosition[positionId].push(employeesMap?.[employeeId]);
    })
  );
  return bookedEmployeesByPosition;
};

export const isDisableBookingWithOverBooking = ({
  shift: { needed = 0, stats },
  shift,
  allowOverbooking,
  shouldCheckSupplierOverbooking = false
}) => {
  const shiftBooked = stats?.booked + stats?.bookedStandBy;

  const shiftNeeded = shouldCheckSupplierOverbooking ? shift.clientShift.needed : needed;
  const isDisabled = shiftBooked >= shiftNeeded;

  if (shouldCheckSupplierOverbooking) {
    return isDisabled;
  }

  if (allowOverbooking) {
    return false;
  }

  return isDisabled;
};

export const groupedSchedulingData = ({
  projects,
  mappedLabels,
  mappedClientBranches,
  mappedLocations,
  mappedTags,
  displayGroupedPositions = false,
  sorter,
  timeFormat,
  sort,
  employeesMap,
  showLocationName = false
}) => {
  const sortFunc = (a, b) => (sort?.sortDirection === 'asc' ? sorter(a, b) : sorter(b, a));

  const data = (projects || []).map(
    ({
      id,
      date,
      endDate,
      name,
      clientBranchId,
      locationId,
      invoiceNumber,
      labelId,
      isMultiDay,
      dates,
      tags: tagsIds = [],
      shifts = []
    }) => {
      const projectName = labelId ? mappedLabels[labelId]?.title : name;
      const client = mappedClientBranches?.[clientBranchId]?.name;
      const location = [mappedLocations[locationId]?.name || '', mappedLocations[locationId]?.address || ''].join(', ');
      const tags =
        mappedTags && tagsIds?.length > 0 ? tagsIds.map((tagId) => mappedTags[tagId]?.label)?.join(', ') || '' : '';

      const positions = displayGroupedPositions
        ? dates[date]?.positionIds
        : getGroupedBookedEmployeesByPosition({ shifts, employeesMap });

      // Hack until multi day is supported
      const dateKey = Object.keys(dates || {})?.[0];
      const firstMultiDay = dateKey ? dates?.[dateKey] : {}; // might be empty is no shifts on first day
      const { minTime, maxTime } = firstMultiDay;

      const { totalConfirmed: confirmed, totalBooked: booked, totalNeeded: needed } = Object.values(dates || {}).reduce(
        (prev, { stats }) => {
          prev.totalConfirmed = prev.totalConfirmed + stats?.confirmed;
          prev.totalBooked = prev.totalBooked + stats?.booked;
          prev.totalNeeded = prev.totalNeeded + stats?.needed;
          return prev;
        },
        { totalConfirmed: 0, totalBooked: 0, totalNeeded: 0 }
      );

      return {
        projectId: id,
        tags,
        invoiceNumber,
        date,
        endDate,
        isMultiDay,
        projectName: projectName || (showLocationName ? location : ''),
        client,
        location,
        positions,
        minTime: minTime && moment(minTime).format(timeFormat),
        maxTime: maxTime && moment(maxTime).format(timeFormat),
        needed,
        booked,
        confirmed,
        progress: !needed ? 100 : Math.round((booked / needed) * 100)
      };
    }
  );

  return sort ? data.sort(sortFunc) : data;
};

export const getEmployeesHoursByPeriod = ({ projectInfo, defaultEmployeeWorkingHoursPeriod }) => {
  const selectedType =
    defaultEmployeeWorkingHoursPeriod === EMPLOYEE_WORKING_HOURS_PERIOD_OPTIONS_MAPPED.MONTHLY
      ? PROJECT_INFO_EMPLOYEE_WORKING_HOURS_TYPE.MONTH_HOURS
      : PROJECT_INFO_EMPLOYEE_WORKING_HOURS_TYPE.WEEK_HOURS;

  return projectInfo?.find(({ type }) => type === selectedType)?.metadata?.employees;
};

export const getEmployeeHourWarningByPeriodProjectInfo = ({
  shiftId,
  projectInfo,
  userId,
  defaultEmployeeWorkingHoursPeriod
}) => getEmployeesHoursByPeriod({ projectInfo, defaultEmployeeWorkingHoursPeriod })?.[userId]?.shifts?.[shiftId];

export const getUserHoursByPeriodMetadata = ({ userInfo, defaultEmployeeWorkingHoursPeriod }) => {
  const selectedType =
    defaultEmployeeWorkingHoursPeriod === EMPLOYEE_WORKING_HOURS_PERIOD_OPTIONS_MAPPED.MONTHLY
      ? WARNINGS_AND_INFOS_TYPES.TOTAL_MONTH_HOURS
      : WARNINGS_AND_INFOS_TYPES.TOTAL_WEEK_HOURS;

  return userInfo?.find(({ type }) => type === selectedType)?.metadata;
};

export const getHoursBracketTooltipKey = ({ monthlyHoursLimit, defaultEmployeeWorkingHoursPeriod }) => {
  if (monthlyHoursLimit?.noBaseRate) {
    return 'monthlyLimitNoBaseRate';
  }

  return defaultEmployeeWorkingHoursPeriod === EMPLOYEE_WORKING_HOURS_PERIOD_OPTIONS_MAPPED.MONTHLY
    ? 'totalHoursThisMonth'
    : 'totalHoursThisWeek';
};

export const getSpecificEmployeeStats = ({ projectInfo, userId }) =>
  projectInfo?.find(({ type }) => type === 'EmployeeStats')?.metadata?.employees?.[userId];

export const getEmployeesStats = ({ projectInfo }) =>
  projectInfo?.find(({ type }) => type === 'EmployeeStats')?.metadata?.employees || {};

export const getShiftsInProjectByDate = (project, date) => {
  if (!project) {
    return [];
  }

  return (project?.shifts || []).filter(
    ({ date: shiftDate }) => moment(shiftDate).format(API_DATE_FORMAT) === moment(date).format(API_DATE_FORMAT)
  );
};

/* Disable booking when supplier is overbooking - supplier try to book more then what whas ordered.
  !! NOTE: Supplier overbooking is allowed when
  *. Supplier of a freemium client.
  *. Order init - meaning the supplier opened the event for the client
  *. Supplier external shift - meaning no clientShift or client shift was deleted.
*/
export const getIsSupplierOverbooking = () => {
  // currently feature is disabled due to client feedback
  return;

  // export const getIsSupplierOverbooking = ({ mappedClientBranches, supplierOrder, clientShift }) => {
  // // not a supplier
  // if (!supplierOrder) {
  //   return false;
  // }
  // // supplier for a client freemium
  // if (mappedClientBranches?.[supplierOrder.clientBranchId]?.status === ACCOUNT_BILLING_STATUS_MAP.CLIENT_FREEMIUM) {
  //   return false;
  // }
  // // supplier initiated the order
  // if (supplierOrder.status === ORDER_STATUSES.ORDER_INITIATED.label) {
  //   return false;
  // }
  // // doesn't have client shift connected or the client shift was deleted
  // if (!clientShift || clientShift.isDeleted) {
  //   return false;
  // }
  // return true;
};

export const isProjectMultiDay = ({ date, endDate }) => {
  return endDate ? moment(date).format(API_DATE_FORMAT) !== moment(endDate).format(API_DATE_FORMAT) : false;
};

//  1. In this project, exists one order with status 'init'
//    a. can't edit
//  2. If the none of the order are in status init:
//    a. no client tagged
//      i. I can edit date range
//      ii. All the suppliers projects in the chain will be updated
//    b. there's client tagged
//      i. if the supplierOrder status is init -> I can edit
//      ii. otherwise, no.
export const isEditMultiDayDatesEnabled = ({ project, accountId }) => {
  // ONB HACK to edit single day events to multi day
  if (accountId === ONB_ACCOUNT_ID) {
    return true;
  }
  // Dont allow edit if project is not multi day.
  if (!project?.isMultiDay) {
    return false;
  }

  const isClientOfOrderInit = (project?.orders || []).some(
    ({ status }) => status === ORDER_STATUSES.ORDER_INITIATED.label
  );

  // Dont allow edit if the project has an order that was initiated by the supplier, meaning project.orders[x].status === 'order init'
  if (isClientOfOrderInit) {
    return false;
  }

  /* Dont allow edit if the project has a client and the order in it was not initiated by the supplier,
		meaning project.supplierOrder.status !== 'order init' */
  if (project?.supplierOrder && project.supplierOrder.status !== ORDER_STATUSES.ORDER_INITIATED.label) {
    return false;
  }

  return true;
};

/* Who can adjust (edit) a single shift via the AdjustShiftModal.
   Perspective is derived from the project: I'm the client when it carries `orders`,
   I'm the supplier serving a client when it carries `supplierOrder` (check client first).
   1. No client relationship (no orders, no supplierOrder) -> allow.
   2. Client (orders): allowed only if I'm not freemium AND the order is not init.
   3. Supplier (supplierOrder): freemium client -> I can; otherwise only if I opened the
      event for the client (supplierOrder.status === order init). */
export const getCanAdjustShift = ({ project, mappedClientBranches, billingStatus }) => {
  const { orders = [], supplierOrder } = project || {};

  // I'm the supplier serving a client
  if (supplierOrder) {
    const isClientFreemium =
      mappedClientBranches?.[supplierOrder.clientBranchId]?.status === ACCOUNT_BILLING_STATUS_MAP.CLIENT_FREEMIUM;
    if (isClientFreemium) {
      return true;
    }
    return supplierOrder.status === ORDER_STATUSES.ORDER_INITIATED.label;
  }

  // I'm the client (orders exist against me)
  if (orders.length > 0) {
    const amIFreemium = billingStatus === ACCOUNT_BILLING_STATUS_MAP.CLIENT_FREEMIUM;

    const isOrderInit = orders.some(({ status }) => status === ORDER_STATUSES.ORDER_INITIATED.label);

    return !amIFreemium && !isOrderInit;
  }

  // No client relationship -> allow
  return true;
};

export const getFilteredProjectsShiftsByDate = ({ projects, date }) =>
  (projects || [])
    .filter(({ dates }) => dates?.[moment(date).format(API_DATE_FORMAT)]?.showOnCalendar)
    .map((project) => ({
      ...project,
      shifts: project.mappedShiftsByDay
        ? project.mappedShiftsByDay[moment(date).format(API_DATE_FORMAT)] || []
        : project.shifts,
      allShifts: project.shifts,
      allOrders: project.orders,
      orders: (project.orders || []).map((order) => ({
        ...order,
        shifts: order.mappedOrderShiftsByDay
          ? order.mappedOrderShiftsByDay[moment(date).format(API_DATE_FORMAT)] || []
          : order.shifts
      }))
    }));

export const formatEmployeeSlotBreakPlotsByDate = ({ employeeSlotBreaks, date }) => ({
  added: (employeeSlotBreaks?.added || []).map((item) => {
    const { startTime, endTime } = getTimeRangeWithDates({
      startTime: item.startTime,
      endTime: item.endTime,
      date
    });
    return {
      ...item,
      startTime,
      endTime
    };
  }),
  edited: (employeeSlotBreaks?.edited || []).map((item) => {
    const { startTime, endTime } = getTimeRangeWithDates({
      startTime: item.startTime,
      endTime: item.endTime,
      date
    });
    return {
      ...item,
      startTime,
      endTime
    };
  }),
  deleted: employeeSlotBreaks?.deleted || []
});

export const getEmployeeAgeWarning = ({ age, isAgeHidden = false }) => {
  if (isAgeHidden) {
    return { isUnder18: false, isWarning: false };
  }

  return {
    isUnder18: age ? age < 18 : false,
    isWarning: age ? age >= 77 : false
  };
};

// Single-letter gender badge (e.g. "M"/"F") — the translated value's first character.
export const genderInitial = (t, gender) => (gender ? t(gender).charAt(0) : null);

export const getEmployeeAgeInfo = (birthdateStr, { isAgeHidden = false } = {}) => {
  if (!birthdateStr || isAgeHidden) {
    return {
      age: undefined,
      birthdateFormatted: undefined,
      hasAgeWarning: false
    };
  }

  const birthdateMoment = birthdateStr ? moment(birthdateStr, API_DATE_TIME_FORMAT) : null;
  const birthdateFormatted = birthdateMoment?.isValid() ? birthdateMoment.format(API_DATE_TIME_FORMAT) : undefined;
  const age = birthdateMoment?.isValid() ? moment().diff(birthdateMoment, 'years') : undefined;
  const { isUnder18, isWarning } = getEmployeeAgeWarning({ age, isAgeHidden });

  return {
    age,
    birthdateFormatted,
    hasAgeWarning: age != null && (isUnder18 || isWarning)
  };
};

export const generateBulkCreateOrUpdateEmployeeSlotBreakPlots = ({
  employeeSlotBreaks,
  updateFunction,
  date: initalDate,
  items
}) => {
  if (!updateFunction || !items || items?.length === 0) {
    return [];
  }

  return items
    .map((item) => {
      const employeeSlotId = item?.shiftData?.employeeSlot?.id;
      const date = item?.shiftData?.startTime ? moment(item.shiftData.startTime).format(API_DATE_FORMAT) : initalDate;

      if (!employeeSlotId) {
        return null;
      }
      return updateFunction?.({
        ...formatEmployeeSlotBreakPlotsByDate({
          employeeSlotBreaks,
          date
        }),
        employeeSlotId
      });
    })
    .filter((curr) => !!curr);
};

export const findOrderToShowNoOrderId = (selectedProject, hasUnpublishShifts) => {
  if (!selectedProject) {
    return undefined;
  }

  const { supplierOrder, orders = [], shifts = [] } = selectedProject;
  if (!hasUnpublishShifts) {
    return undefined;
  }

  const isOnlyOrderInClientPendingApproval =
    orders.length === 1 && !supplierOrder && orders[0].status === ORDER_STATUSES.CLIENT_ORDER_PENDING_APPROVAL.label;

  if (isOnlyOrderInClientPendingApproval) {
    return orders[0];
  }

  const isSupplierOrderPendingSupplierApproval =
    !orders.length && supplierOrder && supplierOrder.status === ORDER_STATUSES.SUPPLIER_ORDER_PENDING_APPROVAL.label;

  if (isSupplierOrderPendingSupplierApproval) {
    // THIS IS ORDER MODAL DETAILS FROM SUPPLIER POV (shifts are derived from the project)
    return { ...supplierOrder, shifts };
  }

  return undefined;
};

export const findOrderToShowWithOrderId = (selectedProject, orderId) => {
  if (!selectedProject) {
    return undefined;
  }

  if (!orderId) {
    return undefined;
  }

  const { supplierOrder, orders = [], shifts = [] } = selectedProject;

  if (supplierOrder?.id === parseInt(orderId)) {
    // THIS IS ORDER MODAL DETAILS FROM SUPPLIER POV (shifts are derived from the project)
    return { ...supplierOrder, shifts };
  }

  if (orders.length > 0) {
    const relevantSupplierOrder = orders.find(({ id }) => id === parseInt(orderId));

    // THIS IS ORDER MODAL DETAILS FROM CLIENT POV (shifts are derived from the order)
    if (relevantSupplierOrder) {
      return relevantSupplierOrder;
    }
  }

  return undefined;
};

/* Hack to fix empty save shift audience */
export const formatShiftAudience = (shiftAudienceData) => {
  /* in case shiftAudienceData is undefined or equals to {} */
  if (!shiftAudienceData || Object.entries(shiftAudienceData) === 0) {
    return shiftAudienceData;
  }
  const isEmptyAudience =
    !!shiftAudienceData &&
    shiftAudienceData?.audience?.all === null &&
    shiftAudienceData?.audience?.exclude === null &&
    shiftAudienceData?.audience?.include === null &&
    shiftAudienceData?.filters?.basic == null;

  return isEmptyAudience ? {} : shiftAudienceData;
};

/**
 * Filters tag IDs to only include those that exist in the available tags options.
 * This is useful for filtering out deleted tags from events/templates.
 *
 * Uses a Set for O(1) lookup performance with large lists of tags.
 *
 * @param {Array<number|string>} tagIds - Array of tag IDs to filter
 * @param {Array<{value: number|string}>} tagsOptions - Array of available tag options with 'value' property
 * @returns {Array<number|string>} Filtered array containing only valid tag IDs
 */
export const filterValidTagIds = (tagIds, tagsOptions) => {
  if (!Array.isArray(tagIds) || tagIds.length === 0) {
    return [];
  }

  if (!Array.isArray(tagsOptions) || tagsOptions.length === 0) {
    return [];
  }

  const validTagIdSet = new Set(tagsOptions.map((tag) => tag.value));
  return tagIds.filter((tagId) => validTagIdSet.has(tagId));
};

export const getPositionsStyleByParams = ({
  full = false,
  showDateColumn = false,
  showLabelField = false,
  showFlatWage = false,
  showPayroll = false,
  showCostCenter = false,
  showClientCostCenter = false,
  showActions = true,
  showOffsetColumn = false
}) => {
  let gridTemplateColumns = [];
  let gridTemplateAreas = [];
  if (full) {
    if (showDateColumn) {
      gridTemplateColumns.push('3fr');
      gridTemplateAreas.push('date');
    }

    gridTemplateAreas.push('position');
    gridTemplateColumns.push('3fr');

    if (showLabelField) {
      gridTemplateColumns.push('3fr');
      gridTemplateAreas.push('label');
    }

    gridTemplateAreas.push(`audience time${showOffsetColumn ? ' offsetTime' : ''} qty location comments`);
    gridTemplateColumns.push(`1fr 4fr${showOffsetColumn ? ' 3fr' : ''} 1fr 2fr 3fr`);

    if (showCostCenter) {
      gridTemplateColumns.push('2fr');
      gridTemplateAreas.push('costCenter');
      if (showClientCostCenter) {
        gridTemplateColumns.push('2fr');
        gridTemplateAreas.push('clientCostCenter');
      }
    }

    gridTemplateAreas.push('uniform');
    gridTemplateColumns.push('2fr');

    if (showPayroll) {
      gridTemplateColumns.push('1fr');
      gridTemplateAreas.push('hourlyWage');
    }

    if (showFlatWage) {
      gridTemplateColumns.push('repeat(2, 1fr)');
      gridTemplateAreas.push('flatWage flatHours');
    }

    gridTemplateColumns.push('1fr');
    gridTemplateAreas.push('actions');
  } else {
    if (showDateColumn) {
      gridTemplateColumns.push('3fr');
      gridTemplateAreas.push('date');
    }

    gridTemplateAreas.push('position');

    gridTemplateColumns.push('3fr');

    if (showLabelField) {
      gridTemplateColumns.push('3fr');
      gridTemplateAreas.push('label');
    }

    gridTemplateAreas.push(`audience time${showOffsetColumn ? ' offsetTime' : ''} qty location comments`);
    gridTemplateColumns.push(`1fr 4fr${showOffsetColumn ? ' 3fr' : ''} 1fr 2fr 3fr`);

    if (showCostCenter) {
      gridTemplateColumns.push('1fr');
      gridTemplateAreas.push('costCenter');
      if (showClientCostCenter) {
        gridTemplateColumns.push('2fr');
        gridTemplateAreas.push('clientCostCenter');
      }
    }

    if (showActions) {
      gridTemplateColumns.push('1fr');
      gridTemplateAreas.push('actions');
    }
  }
  return { gridTemplateColumns, gridTemplateAreas };
};

export const bookToEntireEventPromptCalculation = ({ data, shortDateFormat, timeFormat }) => {
  const results = {
    hasAnyShifts: !!data?.length,
    canBookAnyway: true,
    ok: [],
    wtd: [],
    wtdRules: [],
    compliance: [],
    dateBasedWarnings: [],
    noAvailableSlot: [],
    overbooked: [],
    alreadyBookedSameDay: []
  };

  if (data?.length) {
    data.forEach((shift) => {
      const date = moment(shift.shiftDate).format(shortDateFormat);
      const isOk =
        !shift.wtd &&
        !shift.compliance &&
        !shift.dateBasedWarnings?.length &&
        !shift.overbooking &&
        !shift.noAvailableSlot &&
        !shift.alreadyBookedSameDay;
      results.canBookAnyway = !!shift.canBookAnyway;
      if (isOk) {
        const dateTimeString = `${date}, ${moment(shift.shiftStartTime).format(timeFormat)}${
          shift.shiftEndTime ? ` - ${moment(shift.shiftEndTime).format(timeFormat)}` : ''
        }`;

        results.ok.push(dateTimeString);
      }
      if (shift.wtd) {
        results.wtd.push(date);
        results.wtdRules.push(shift.wtd);
      }
      if (shift.dateBasedWarnings?.length) {
        results.dateBasedWarnings.push(date);
      }
      if (shift.compliance) {
        results.compliance.push(date);
      }
      if (shift.overbooking) {
        results.overbooked.push(date);
      }
      if (shift.noAvailableSlot) {
        results.noAvailableSlot.push(date);
      }
      if (shift.alreadyBookedSameDay) {
        results.alreadyBookedSameDay.push(date);
      }
    });
  }
  return results;
};

export const calculateTopScrollTo = ({ weekIndex }) => {
  const top = Array.from({ length: weekIndex }).reduce(
    (total, _, i) => total + document.getElementById(`month-view-row-${i}`)?.clientHeight + 2,
    0
  );

  return top;
};

// This is external options for every filter group - positions, locations...
export const getShiftsFilterOptions = ({
  shifts,
  mappedSupplierPositions,
  mappedLocations,
  mappedPositions,
  mappedSuppliers,
  mappedCostCenters,
  t
}) => {
  const uniquePositionIds = new Set();
  const uniqueLocationIds = new Set();
  const uniqueStartTimes = new Set();
  const uniqueEndTimes = new Set();
  const uniqueSupplierAccountIds = new Set();
  const uniqueBranchCostCenterIds = new Set();

  return shifts
    .filter(({ mappedToAnyShiftInCurrentProject }) =>
      mappedToAnyShiftInCurrentProject ? mappedToAnyShiftInCurrentProject.length === 0 : true
    )
    .reduce(
      (
        output,
        {
          branchCostCenterId,
          positionId,
          locationId,
          isThirdParty,
          startTime,
          endTime,
          isExternal,
          supplierAccountId,
          mappingWithSuppliersAccount,
          positionSlug
        }
      ) => {
        let relevantMappings = isExternal
          ? { positions: mappedSupplierPositions, locations: mappedLocations }
          : { positions: mappedPositions, locations: mappedLocations, mappedCostCenters };

        const formattedPosition = positionId || null;

        if (!uniquePositionIds.has(formattedPosition)) {
          uniquePositionIds.add(formattedPosition);
          output[PROJECT_SCHEDULING_FILTERS_SLUGS.positions].push({
            id: formattedPosition,
            value: isThirdParty
              ? t(positionSlug)
              : relevantMappings.positions[formattedPosition]?.slug || t(POSITIONS_OPTIONS.NO_POSITION)
          });
        }

        const formattedLocation = relevantMappings.locations[locationId] ? locationId : null;

        if (!uniqueLocationIds.has(formattedLocation)) {
          uniqueLocationIds.add(formattedLocation);
          output[PROJECT_SCHEDULING_FILTERS_SLUGS.locations].push({
            id: formattedLocation,
            value: relevantMappings?.locations[locationId]?.name || t(AREAS_OPTIONS.OTHER_AREAS)
          });
        }

        const formattedStartTimeValue = startTime && moment.utc(startTime).format('HH:mm:ss');
        const formattedEndTimeValue = endTime && moment.utc(endTime).format('HH:mm:ss');

        if (formattedStartTimeValue && !uniqueStartTimes.has(formattedStartTimeValue)) {
          uniqueStartTimes.add(formattedStartTimeValue);
          output[PROJECT_SCHEDULING_FILTERS_SLUGS.startTime].push({
            id: formattedStartTimeValue,
            value: formattedStartTimeValue
          });
        }

        if (formattedEndTimeValue && !uniqueEndTimes.has(formattedEndTimeValue)) {
          uniqueEndTimes.add(formattedEndTimeValue);
          output[PROJECT_SCHEDULING_FILTERS_SLUGS.endTime].push({
            id: formattedEndTimeValue,
            value: formattedEndTimeValue
          });
        }

        if (branchCostCenterId && !uniqueBranchCostCenterIds.has(branchCostCenterId)) {
          uniqueBranchCostCenterIds.add(branchCostCenterId);
          output[PROJECT_SCHEDULING_FILTERS_SLUGS.branchCostCenters].push({
            id: branchCostCenterId,
            value: relevantMappings.mappedCostCenters[branchCostCenterId]?.name
          });
        }

        if (mappingWithSuppliersAccount) {
          Object.keys(mappingWithSuppliersAccount).forEach((rawSupplierId) => {
            const supplierId = Number(rawSupplierId);
            if (!uniqueSupplierAccountIds.has(supplierId) && mappedSuppliers[supplierId]) {
              uniqueSupplierAccountIds.add(supplierId);
              output[PROJECT_SCHEDULING_FILTERS_SLUGS.suppliers].push({
                id: Number(supplierId),
                value: mappedSuppliers[supplierId].name
              });
            }
          });
        }
        if (isExternal && supplierAccountId && !uniqueSupplierAccountIds.has(supplierAccountId)) {
          if (mappedSuppliers[supplierAccountId]) {
            uniqueSupplierAccountIds.add(supplierAccountId);
            output[PROJECT_SCHEDULING_FILTERS_SLUGS.suppliers].push({
              id: supplierAccountId,
              value: mappedSuppliers[supplierAccountId].name
            });
          }
        }
        return output;
      },
      {
        [PROJECT_SCHEDULING_FILTERS_SLUGS.positions]: [],
        [PROJECT_SCHEDULING_FILTERS_SLUGS.locations]: [],
        [PROJECT_SCHEDULING_FILTERS_SLUGS.startTime]: [],
        [PROJECT_SCHEDULING_FILTERS_SLUGS.endTime]: [],
        [PROJECT_SCHEDULING_FILTERS_SLUGS.suppliers]: [],
        [PROJECT_SCHEDULING_FILTERS_SLUGS.branchCostCenters]: []
      }
    );
};

const formatShiftsFilters = ({ disabledFilters, filters = {} }) => {
  return (filters.basic || []).reduce((output, { slug, value }) => {
    if (disabledFilters.includes(slug)) return output;

    switch (slug) {
      case PROJECT_SCHEDULING_FILTERS_SLUGS.positions:
        if (!output.positionIds) output.positionIds = [];
        output.positionIds = value;
        break;
      case PROJECT_SCHEDULING_FILTERS_SLUGS.locations:
        if (!output.locationIds) output.locationIds = [];
        output.locationIds = value;
        break;
      case PROJECT_SCHEDULING_FILTERS_SLUGS.mainLocations:
        output.mainLocationIds = value;
        break;
      case PROJECT_SCHEDULING_FILTERS_SLUGS.areas:
        output.areaIds = value;
        break;
      case PROJECT_SCHEDULING_FILTERS_SLUGS.startTime:
        if (!output.startTime) output.startTime = [];
        output.startTime = value;
        break;
      case PROJECT_SCHEDULING_FILTERS_SLUGS.endTime:
        if (!output.endTime) output.endTime = [];
        output.endTime = value;
        break;
      case PROJECT_SCHEDULING_FILTERS_SLUGS.suppliers:
        if (!output.supplierAccountIds) output.supplierAccountIds = [];
        output.supplierAccountIds = value;
        break;
      case PROJECT_SCHEDULING_FILTERS_SLUGS.bookingStatus:
        if (!output.bookingStatus) output.bookingStatus = [];
        output.bookingStatus = value;
        break;
      case PROJECT_SCHEDULING_FILTERS_SLUGS.branchCostCenters:
        if (!output.branchCostCenterIds) output.branchCostCenterIds = [];
        output.branchCostCenterIds = value;
        break;
      case PROJECT_SCHEDULING_FILTERS_SLUGS.bookingIssues:
        if (!output.bookingIssues) output.bookingIssues = [];
        output.bookingIssues = value;
        break;
      case PROJECT_SCHEDULING_FILTERS_SLUGS.ageWarning:
        if (!output.ageWarning) output.ageWarning = [];
        output.ageWarning = value;
        break;
      case PROJECT_SCHEDULING_FILTERS_SLUGS.liveViewStatus:
        if (!output.liveViewStatus) output.liveViewStatus = [];
        output.liveViewStatus = value;
        break;
      default:
        return output;
    }
    return output;
  }, {});
};

export const getSlotLiveViewStatuses = ({ slot, timezone, accountId, now }) => {
  const { breaktimes = [] } = slot?.timesheet || {};
  const mostUpdatedBreak = getLatestBreak(breaktimes);

  const flags = getAttendanceFlags({
    shiftStartTime: slot?.startTime,
    shiftEndTime: slot?.endTime,
    startTime: slot?.timesheet?.startTime,
    endTime: slot?.timesheet?.endTime,
    // Check in / Check out shift an employee out of the Clocked in bucket and into Checked in /
    // Checked out — the same precedence getLiveViewAttendanceStats feeds the Live View tab's pie.
    checkInTime: slot?.timesheet?.checkInTime,
    checkOutTime: slot?.timesheet?.checkOutTime,
    breakStart: mostUpdatedBreak?.startTime,
    breakEnd: mostUpdatedBreak?.endTime,
    timezone,
    accountId,
    now
  });

  return Object.keys(LIVE_VIEW_FILTER_STATUSES).filter((flag) => flags[flag]);
};

// Single place that applies a selected Live View Status filter to a list of booked slots — reused by
// the Event Index shift-level filter (filterShiftsData below), Booked.js's internal roster, and
// External.js's order/supplier roster, so none of them re-derive the Set/clock/matching logic.
export const filterSlotsByLiveViewStatus = ({ slots = [], liveViewStatusFilter, timezone, accountId }) => {
  if (!liveViewStatusFilter?.length) return slots;

  const liveViewStatusSet = new Set(liveViewStatusFilter);

  // Resolved once here, not per slot: getAttendanceFlags has its own now-or-compute-it fallback, but
  // relying on that would call getAttendanceNow() once per slot instead of once per this call — every
  // slot checked in this pass needs the same instant anyway.
  const now = getAttendanceNow(timezone);

  return slots.filter((slot) =>
    getSlotLiveViewStatuses({ slot, timezone, accountId, now }).some((status) => liveViewStatusSet.has(status))
  );
};

export const filterShiftsData = ({
  shifts = [],
  filters: basicFilters = {},
  disabledFilters = [],
  mappedLocations = {},
  accountId,
  orderShiftSlotsByShiftId = {},
  projectLocationId,
  isRawShifts = false
}) => {
  const filters = formatShiftsFilters({ filters: basicFilters, disabledFilters });

  return shifts.filter((shift) => {
    const {
      id: shiftId,
      mappedToAnyShiftInCurrentProject,
      branchCostCenterId,
      positionId,
      locationId,
      startTime,
      endTime,
      supplierAccountId,
      mappingWithSuppliersAccount,
      needed,
      stats,
      isExternal,
      slots = [],
      areaId: slotAreaId
    } = shift;

    // Don't filter OFS
    if (isExternal && mappedToAnyShiftInCurrentProject?.length) return true;

    if (filters?.branchCostCenterIds && !filters.branchCostCenterIds.includes(branchCostCenterId)) return false;

    if (filters?.positionIds && !filters.positionIds.includes(positionId)) return false;

    if (filters?.locationIds && !filters.locationIds.includes(locationId)) return false;

    if (filters?.mainLocationIds || filters?.areaIds) {
      const { mainLocationId, areaId } = getShiftPlaceIds({
        locationId,
        areaId: slotAreaId,
        projectLocationId,
        isRawShifts
      });

      if (
        !isMatchingLocationFilters({
          mainLocationId,
          areaId,
          mainLocationIds: filters.mainLocationIds,
          areaIds: filters.areaIds
        })
      )
        return false;
    }

    if (filters?.startTime && !filters.startTime.includes(moment(startTime).format(API_TIME_FORMAT))) return false;

    if (filters?.endTime && !filters.endTime.includes(moment(endTime).format(API_TIME_FORMAT))) return false;

    if (filters?.supplierAccountIds) {
      const suppliersOFS = new Set([
        ...Object.keys(mappingWithSuppliersAccount || {}).map((supplierId) => Number(supplierId))
      ]);

      const isSupplierExistInFilter = filters.supplierAccountIds.some(
        (supplierId) => supplierAccountId === supplierId || suppliersOFS.has(supplierId)
      );

      if (!isSupplierExistInFilter) return false;
    }

    if (filters?.bookingStatus) {
      let { booked } = stats || {};

      if (isExternal) {
        booked = slots.reduce((acc, slot) => {
          const { employeeId } = slot || {};
          if (employeeId) return acc + 1;
          return acc;
        }, 0);
      }

      const isShiftBookingStatusExistInFilter = filters.bookingStatus.some((bookingStatus) => {
        switch (bookingStatus) {
          case NON_BOOKED_BOOKING_STATUS:
            return booked === 0;
          case PARTIALLY_BOOKED_BOOKING_STATUS:
            return booked > 0 && booked < needed;
          case FULLY_BOOKED_BOOKING_STATUS:
            return booked >= needed;
          default:
            return false;
        }
      });

      if (!isShiftBookingStatusExistInFilter) return false;
    }

    if (filters?.ageWarning) {
      const ageFilterValues = filters.ageWarning;

      const isShiftMatchingAgeWarnings =
        (ageFilterValues.includes(UNDER_18_WARNING) && shift?.ageWarnings?.hasUnder18) ||
        (ageFilterValues.includes(OVER_77_WARNING) && shift?.ageWarnings?.hasOver77);

      if (!isShiftMatchingAgeWarnings) return false;
    }

    if (filters?.liveViewStatus?.length) {
      const timezone = mappedLocations?.[projectLocationId]?.timezoneName;
      // A shift's true roster isn't just its own slots — employees supplied through a supplier order
      // (mapped back via mappedToAnyShiftInCurrentProject) count too, same as what Booked.js renders.
      // Unbooked slots (no employeeId) are excluded — an open position isn't "late" or "absent".
      const allSlots = slots.concat(orderShiftSlotsByShiftId[shiftId] || []).filter(({ employeeId }) => employeeId);

      // filterSlotsByLiveViewStatus resolves its own shared clock once per call (i.e. once per shift),
      // so every slot in this shift is judged against the same instant.
      const hasMatchingSlot = filterSlotsByLiveViewStatus({
        slots: allSlots,
        liveViewStatusFilter: filters.liveViewStatus,
        timezone,
        accountId
      }).length;

      if (!hasMatchingSlot) return false;
    }

    if (filters?.bookingIssues) {
      return filterShiftsByStatus([shift], filters.bookingIssues);
    }

    return true;
  });
};

export const filterShiftsByStatus = (shifts = [], statusFilter) => {
  if (!statusFilter?.length) return true;

  let hasError = false;
  let hasWarning = false;
  let hasOk = false;

  for (const shift of shifts) {
    // Dont filter OFS
    if (shift?.mappedToAnyShiftInCurrentProject?.length) {
      continue;
    }

    const hasErrors = shift?.hasErrors || shift?.clientShift?.isDeleted;
    const hasClientChanges =
      !shift.orderId &&
      shift?.clientShift &&
      (shift.clientShift.needed !== shift?.needed ||
        !moment(shift?.startTime).isSame(moment(shift.clientShift.startTime)) ||
        !moment(shift?.endTime).isSame(moment(shift.clientShift.endTime)));
    const hasWarnings = shift?.hasWarnings || hasClientChanges;

    if (hasErrors) {
      hasError = true;
    }

    if (hasWarnings) {
      hasWarning = true;
    }

    if (!hasErrors && !hasWarnings) {
      hasOk = true;
    }

    // If all statuses (error, warning, and ok) are detected, exit early
    if (hasError && hasWarning && hasOk) {
      break;
    }
  }

  return (
    (statusFilter.includes('ok') && hasOk) ||
    (statusFilter.includes('error') && hasError) ||
    (statusFilter.includes('warning') && hasWarning)
  );
};

export const generateSupplierPositionForOrder = ({ supplier, clientPositions, t, supplierBranchId }) => {
  if (!supplier) return [];

  if (supplier.isClientControlsRates) {
    const mappedClientPositionsToSupplierByClient = supplier?.mappedClientPositionsToSupplierByType?.byClient || {};

    /* show client position name, but with supplier position id */
    return (clientPositions || [])
      .filter(({ id: clientPositionId }) => !!mappedClientPositionsToSupplierByClient[clientPositionId])
      .map(({ id: clientPositionId, slug: clientPositionSlug }) => {
        const mappedSupplierPositionId = mappedClientPositionsToSupplierByClient[clientPositionId];

        return {
          id: mappedSupplierPositionId,
          value: mappedSupplierPositionId,
          label: t(clientPositionSlug),
          slug: clientPositionSlug,
          hourlyWage:
            (supplier?.wages || []).find(({ positionId }) => positionId === mappedSupplierPositionId)?.wage
              ?.hourlyWage || 0
        };
      });
  }

  return (supplier.positions || [])
    .filter(({ isSharedWithClient }) => !!isSharedWithClient)
    .filter(({ branchIds }) => !supplierBranchId || !branchIds?.length || branchIds.includes(supplierBranchId))
    .map(({ id, slug }) => ({
      id,
      value: id,
      label: t(slug),
      slug,
      hourlyWage: (supplier?.wages || []).find(({ positionId }) => positionId === id)?.wage?.hourlyWage || 0
    }))
    .sort(SORT_BY_STRING('label'));
};

export const isShiftDisabledForOrderSelect = ({ shift, supplier }) => {
  let disableShiftSelect = false;
  const isNew = shift.id < 0;

  if (shift.disableShiftSelect || isNew) disableShiftSelect = true;

  if (!supplier || !supplier.isClientControlsRates) {
    return { disableShiftSelect, noMappingToShiftPosition: false };
  }

  /* disable shift select if:
        1. no position
        2. no mapping to the shift position in the supplier side */

  if (!shift.positionId) {
    return { disableShiftSelect: true, noMappingToShiftPosition: true };
  }

  const mappedClientPositionsToSupplierByClient = supplier?.mappedClientPositionsToSupplierByType?.byClient || {};
  const existsMappingToShiftPosition = mappedClientPositionsToSupplierByClient[shift.positionId];

  return {
    disableShiftSelect: disableShiftSelect || !existsMappingToShiftPosition,
    noMappingToShiftPosition: !existsMappingToShiftPosition
  };
};

export const getMatchingSupplierPositionIdByClientPositionId = ({ clientPositionId, supplier }) => {
  if (!supplier.isClientControlsRates) return null;

  if (clientPositionId) {
    return supplier?.mappedClientPositionsToSupplierByType?.byClient[clientPositionId] || null;
  }

  return null;
};
export const getMatchingClientPositionIdBySupplerPositionId = ({ supplierPositionId, supplier }) => {
  if (!supplier.isClientControlsRates) return null;

  if (supplierPositionId) {
    return supplier?.mappedClientPositionsToSupplierByType?.bySupplier[supplierPositionId] || null;
  }

  return null;
};

//todo handle in change event page / positIons
export const SUPPORTED_KEYS_FOR_INITIATE_ORDER_UPDATE = ['startTime', 'endTime', 'locationId', 'comments'];
export const SUPPORTED_KEYS_FOR_CHANGE_INTERNAL_SHIFTS = ['startTime', 'endTime', 'locationId', 'needed'];
export const SHIFT_CHANGES_PROPS_NOTIFY_EMPLOYEES = ['startTime', 'endTime'];

export const hasShiftTimeChanged = (prevShift, nextShift) => {
  const format = (time) => (time ? moment(time).format('HH:mm') : null);

  return SHIFT_CHANGES_PROPS_NOTIFY_EMPLOYEES.some((prop) => format(prevShift?.[prop]) !== format(nextShift?.[prop]));
};

export const RELEVANT_ORDER_STATUS_BULK_CHANGE = [
  ORDER_STATUSES.DRAFT.label,
  ORDER_STATUSES.SUPPLIER_ORDER_APPROVED.label,
  ORDER_STATUSES.SUPPLIER_ORDER_PENDING_APPROVAL.label,
  ORDER_STATUSES.CHANGES_REQUESTED.label
];

export const checkIfInitiateOrderChange = ({
  mappingWithSuppliersAccount,
  changedProp,
  shift,
  mappedLocations,
  orders
}) => {
  const isOFSShift = !isEmpty(mappingWithSuppliersAccount || {});

  if (!isOFSShift) return false;

  /* enable add change in orders only if the order can be changed from client side (status)  + there is OFS to them */
  const relevantOrders = (orders || []).filter(
    ({ supplierAccountId }) =>
      !!mappingWithSuppliersAccount?.[supplierAccountId] &&
      mappingWithSuppliersAccount[supplierAccountId].includes(shift.id)
  );

  if (relevantOrders.length === 0) return false;

  /* is the new selected location of shift is not sub location, do not initiate order change */
  if (changedProp === 'locationId' && !!shift && !mappedLocations[shift.locationId]?.isSubLocation) return false;

  return SUPPORTED_KEYS_FOR_INITIATE_ORDER_UPDATE.includes(changedProp);
};

export const getUniqueObjectsById = (array) => {
  const map = new Map();

  array.forEach((obj) => {
    // Add object to the map, using its id as the key
    map.set(obj.id, obj);
  });

  // Convert the map values back to an array
  return Array.from(map.values());
};

export const getOrderedEmployeesBySupplierId = (shifts) =>
  (shifts || []).reduce((output, { orderedToShiftStats = {} }) => {
    if (!orderedToShiftStats.stats) {
      return output;
    }

    Object.entries(orderedToShiftStats.stats || {}).forEach(([supplierAccountId, { needed }]) => {
      output[supplierAccountId] = (output[supplierAccountId] || 0) + needed;
    });

    return output;
  }, {});

// Group-by select options shared by the booking-board Live View tab and the swap-shift picker.
export const getProjectGroupByOptions = (t) => [
  { value: PROJECT_GROUP_BY_OPTIONS.DATE, label: t('shiftsGroupByDate') },
  { value: PROJECT_GROUP_BY_OPTIONS.LOCATION, label: t('shiftsGroupByArea') },
  { value: PROJECT_GROUP_BY_OPTIONS.POSITION, label: t('shiftsGroupByPosition') },
  { value: PROJECT_GROUP_BY_OPTIONS.START_TIME, label: t('shiftsGroupByStartTime') },
  { value: PROJECT_GROUP_BY_OPTIONS.END_TIME, label: t('shiftsGroupByEndTime') },
  { value: PROJECT_GROUP_BY_OPTIONS.COST_CENTER, label: t('shiftsGroupByCostCenter') }
];

// Comparator for shift groups: by start/end time for the time group-bys, otherwise by header name.
// Shared so the Live View tab and the swap picker sort groups identically.
export const getShiftGroupsComparator = (groupBy) => (a, b) => {
  if (groupBy === PROJECT_GROUP_BY_OPTIONS.START_TIME || groupBy === PROJECT_GROUP_BY_OPTIONS.END_TIME) {
    const timeKey = groupBy === PROJECT_GROUP_BY_OPTIONS.START_TIME ? 'startTime' : 'endTime';
    return SORT_STRING(a.header[timeKey], b.header[timeKey]);
  }
  return SORT_STRING(String(a.header?.name ?? ''), String(b.header?.name ?? ''));
};

export const generateGroupShiftsProperties = ({
  t,
  groupBy,
  shift,
  mappedLocations,
  mappedSupplierPositions,
  mappedPositions,
  costCenters = [],
  hoursFormatter,
  dateFormat
}) => {
  const shiftDate = moment(shift.date).format(API_DATE_FORMAT);
  let key = shiftDate;

  const addNewProperties = {
    date: shiftDate
  };

  const header = {
    type: groupBy
  };

  switch (groupBy) {
    case PROJECT_GROUP_BY_OPTIONS.LOCATION:
      key = shift.locationId && mappedLocations[shift.locationId]?.name ? shift.locationId : AREAS_OPTIONS.OTHER_AREAS;
      header.name = !shift.locationId
        ? t('noLocation')
        : mappedLocations[shift.locationId]?.name || t(AREAS_OPTIONS.OTHER_AREAS);
      addNewProperties.locationId = shift.locationId;
      break;
    case PROJECT_GROUP_BY_OPTIONS.LAT_LNG: {
      const location = mappedLocations?.[shift?.locationId];
      key = location?.lat && location?.lng ? `${location.lat}|${location.lng}` : null;

      header.name = key;
      addNewProperties.locationId = shift.locationId;

      break;
    }

    case PROJECT_GROUP_BY_OPTIONS.POSITION: {
      const positionName = !shift.positionId
        ? 'noPosition'
        : mappedPositions[shift.positionId]?.label ||
          mappedSupplierPositions[shift.positionId]?.label ||
          t(shift.positionSlug) ||
          t('other');

      key = positionName;
      addNewProperties.positionName = positionName;
      header.name = !shift.positionId
        ? t('noPosition')
        : mappedPositions[shift.positionId]?.label ||
          mappedSupplierPositions[shift.positionId]?.label ||
          t(shift.positionSlug) ||
          t('other');
      break;
    }
    case PROJECT_GROUP_BY_OPTIONS.START_TIME:
      key = hoursFormatter(shift.startTime);
      addNewProperties.startTime = shift.startTime;
      header.name = hoursFormatter(shift.startTime);
      header.startTime = moment(shift.startTime).format('HH:mm');
      break;
    case PROJECT_GROUP_BY_OPTIONS.END_TIME:
      key = hoursFormatter(shift.endTime);
      addNewProperties.endTime = shift.endTime;
      header.name = hoursFormatter(shift.endTime);
      header.endTime = moment(shift.endTime).format('HH:mm');
      break;
    case PROJECT_GROUP_BY_OPTIONS.COST_CENTER:
      key = shift.branchCostCenterId || COST_CENTER_OPTIONS.NO_COST_CENTER;
      addNewProperties.branchCostCenterId = shift.branchCostCenterId ?? null;
      header.name = shift.branchCostCenterId
        ? costCenters.find((costCenter) => costCenter.id === shift.branchCostCenterId)?.name
        : t('noCostCenter');
      break;
    case PROJECT_GROUP_BY_OPTIONS.DATE:
    default:
      key = shiftDate;
      header.name = moment(shift.date).format(dateFormat);
      break;
  }

  return { key, header, addNewProperties };
};

export const generateUniqueWarningsSlugs = (warnings = []) => {
  // Remove duplicate warnings based on slug
  const seenSlugs = new Set();
  const uniqueWarnings = (warnings || []).filter((warning) => {
    if (seenSlugs.has(warning.slug)) {
      return false;
    }
    seenSlugs.add(warning.slug);
    return true;
  });

  return uniqueWarnings;
};

export const checkShouldRenderWarningBorderAvailableEmployee = ({
  rejected = false,
  unbooked = false,
  warnings = []
}) =>
  rejected ||
  unbooked ||
  warnings?.some((warning) =>
    [
      SLOT_WARNING_TYPES.BOOKED_SAME_DAY.type,
      SLOT_WARNING_TYPES.OVERLAPPING_SHIFTS.type,
      SLOT_WARNING_TYPES.SHORT_REST_TIME.type,
      SLOT_WARNING_TYPES.DAILY_WORKING_HOURS_LIMIT.type,
      SLOT_WARNING_TYPES.PTO.type,
      SLOT_WARNING_TYPES.LEAVE_RECORD.type,
      SLOT_WARNING_TYPES.DAY_OFF.type
    ].includes(warning.type)
  );

export const getUserCardStatsFlags = ({
  stats = {},
  rankingBadges = {},
  isExternal = false,
  isProjectInfoLoading = false,
  isDummyEmployeeSlot = false
}) => {
  const badges = stats?.badges || [];
  const isWithStats = stats && Object.keys(stats).length > 0 && !Array.isArray(stats);
  const isLeaf = (!isWithStats || badges.includes(rankingBadges.LEAF)) && !isExternal && !isProjectInfoLoading;
  const isRookie = badges.includes(rankingBadges.ROOKIE) && !isExternal && !isProjectInfoLoading;
  const isCrown = badges.includes(rankingBadges.CROWN) && !isExternal && !isProjectInfoLoading;
  const noShows = !isProjectInfoLoading && !isDummyEmployeeSlot ? stats?.totalNoShows : 0;
  const totalBooked = stats?.totalBooked || 0;

  return { isLeaf, isRookie, isCrown, noShows, totalBooked };
};

export const getConfirmerDetails = ({ employeeConfirmation, employeesMap, mappedAdmins }) => {
  if (!employeeConfirmation?.confirmedBy) {
    return { confirmedByEmployee: null, confirmedByAdmin: null };
  }

  const confirmedByEmployee =
    employeeConfirmation.confirmedByType === CONFIRMED_BY_TYPE.EMPLOYEE
      ? employeesMap[employeeConfirmation.confirmedBy]
      : null;

  const confirmedByAdmin =
    employeeConfirmation.confirmedByType === CONFIRMED_BY_TYPE.ADMIN
      ? mappedAdmins[employeeConfirmation.confirmedBy]
      : null;

  return { confirmedByEmployee, confirmedByAdmin };
};

// Shared slot confirmation derivation (used by Booked's getConfirmationSlot and the weekly day cell).
export const getSlotConfirmation = ({ slot, confirmations, accountId, isUbeyaAdmin, employeesMap, mappedAdmins }) => {
  if (!confirmations?.length || !slot?.isConfirmed) {
    return { hasConfirmations: false, isConfirmedSlot: false };
  }

  const lastConfirmation = confirmations[confirmations?.length - 1];
  const employeeConfirmation = lastConfirmation?.employees?.find(
    ({ employeeId }) => employeeId === slot?.employeeSlot?.employeeId
  );

  const { confirmedByEmployee, confirmedByAdmin } = getConfirmerDetails({
    employeeConfirmation,
    employeesMap,
    mappedAdmins
  });

  return {
    confirmationId: lastConfirmation?.id,
    hasConfirmations: accountId !== DELAWARE_NORTH_ID || isUbeyaAdmin,
    isConfirmedSlot: employeeConfirmation?.isConfirmed || false,
    confirmedAt: employeeConfirmation?.createdAt,
    confirmedByEmployee,
    confirmedByAdmin
  };
};

// Shared confirmation tooltip logic (used by ConfirmationButton and the weekly day cell).
export const getConfirmationStatusTooltip = ({
  isEnabled,
  isConfirmed,
  confirmedByEmployee,
  confirmedByAdmin,
  confirmedAt,
  dateFormat,
  t,
  isEditable = true
}) => {
  if (!isEnabled) {
    return t('confirmed');
  }

  if (isEditable && !isConfirmed) {
    return t('bookingConfirmTooltip');
  }

  // Confirmed but no confirmer metadata (backward compatibility)
  if (isEditable && !confirmedByEmployee && !confirmedByAdmin) {
    return t('bookingUnConfirmTooltip');
  }

  return getConfirmationTooltip({ confirmedAt, confirmedByEmployee, confirmedByAdmin, dateFormat, t });
};

export const getConfirmationTooltip = ({ confirmedAt, confirmedByEmployee, confirmedByAdmin, dateFormat, t }) => {
  if (!confirmedAt) {
    return t('confirmed');
  }

  const formattedDate = moment(confirmedAt).format(dateFormat);
  const confirmer = confirmedByEmployee || confirmedByAdmin;

  if (confirmer?.firstName || confirmer?.lastName) {
    const name = [confirmer.firstName, confirmer.lastName].filter(Boolean).join(' ');
    return t('confirmedBy', { name, date: formattedDate });
  }

  return t('confirmed');
};

/**
 * Determines if client cost center field should be shown.
 * For COMPASS_DE organization: only show if client is tagged AND client is part of COMPASS_DE organization.
 * For other organizations: show if feature is enabled.
 */
export const shouldShowClientCostCenter = ({
  enableClientCostCenterFeature,
  organizationId,
  clientBranchId,
  mappedClientBranches
}) => {
  if (!enableClientCostCenterFeature) {
    return false;
  }

  // For non-COMPASS_DE organizations, just check if feature is enabled
  if (organizationId !== COMPASS_DE_ORGANIZATION_ID) {
    return true;
  }

  // For COMPASS_DE: only show if client is tagged AND client is part of COMPASS_DE organization
  const clientOrganizationId = clientBranchId ? mappedClientBranches[clientBranchId]?.organizationId : null;
  return clientOrganizationId === COMPASS_DE_ORGANIZATION_ID;
};

/**
 * Computes whether a shift has approved timesheets or pay queries based on its slots
 */
export const getShiftApprovalStatus = (shift) => {
  return (shift?.slots || []).reduce(
    (acc, { timesheet, payrollProcess }) => {
      if (timesheet?.approved) {
        acc.hasApprovedTimesheets = true;
      }
      if (payrollProcess?.status === PAYROLL_PROCESS_STATUS.PROCESSED) {
        acc.hasProcessedPayroll = true;
      }
      if (payrollProcess?.status === PAYROLL_PROCESS_STATUS.PAY_QUERY) {
        acc.hasPayQuery = true;
      }
      return acc;
    },
    { hasApprovedTimesheets: false, hasPayQuery: false, hasProcessedPayroll: false }
  );
};

/**
 * Determines if a shift can be deleted and provides the reason if not
 */
export const getDisableDeleteShiftCase = (shift, t) => {
  const { hasApprovedTimesheets, hasPayQuery } = getShiftApprovalStatus(shift);

  // 1. the shift has approved timesheets - cannot be deleted
  if (hasApprovedTimesheets) {
    return { value: true, tooltip: t('cantDeleteShiftWithApprovedTimesheets') };
  }

  // 2. the shift has pay query - cannot be deleted
  if (hasPayQuery) {
    return { value: true, tooltip: t('cantDeleteShiftWithPayQuery') };
  }

  // 3. the shift is connected to a client order (supplier side) - cannot be deleted
  if (shift?.internalShiftMappedToClientOFS) {
    return { value: true, tooltip: t('cantDeleteAShiftWithActiveOrders') };
  }

  const shiftOrdersStatuses = Object.values(shift?.orderedToShiftStats?.stats || {});

  // 4. the shift is not connected to any order (client side) -
  const isShiftConnectedToOrder = shiftOrdersStatuses.length > 0;
  if (!isShiftConnectedToOrder) {
    return {
      value: shift?.disableOrderShiftDelete,
      ...(shift?.disableOrderShiftDelete ? { tooltip: t('orderChangesDeletingShiftIsDisabled') } : {})
    };
  }

  // 5. if the shift is connected to orders, all of these orders must be inactive to be able to delete the shift
  const isShiftConnectedOnlyToInactiveOrders = shiftOrdersStatuses.every(({ status }) =>
    INACTIVE_ORDER_TYPES.includes(status)
  );

  return {
    value: !isShiftConnectedOnlyToInactiveOrders,
    ...(!isShiftConnectedOnlyToInactiveOrders ? { tooltip: t('cantDeleteAShiftWithActiveOrders') } : {})
  };
};

/**
 * Wrapper for AG Grid that extracts raw shift from data object

 */
export const getDisableDeleteShift = (data, t) => {
  const shift = data?.raw || data || {};
  return getDisableDeleteShiftCase(shift, t);
};

/**
 * Determines if position editing is disabled for a shift and provides the reason
 */
export const getPositionEditDisabledCase = ({
  shift,
  shiftId,
  positionId,
  readonly,
  orderDetailsContext,
  isUbeyaAdmin,
  isPositionEditDisabledInput,
  t
}) => {
  const hasPosition = !!positionId;
  const { hasApprovedTimesheets, hasPayQuery, hasProcessedPayroll } = getShiftApprovalStatus(shift);

  if (isPositionEditDisabledInput) {
    return { isPositionEditDisabled: true, disabledPositionTooltipBody: t('disablePositionEditReadOnly') };
  }

  if (!!orderDetailsContext && shiftId > 0) {
    return { isPositionEditDisabled: true, disabledPositionTooltipBody: t('disablePositionEditOrderContext') };
  }

  if (readonly) {
    return { isPositionEditDisabled: true, disabledPositionTooltipBody: t('disablePositionEditReadOnly') };
  }

  /* if shift is connected to any shift in client side (if there is order), disable position edit */
  if (shift?.internalShiftMappedToClientOFS && !isUbeyaAdmin) {
    return {
      isPositionEditDisabled: true,
      disabledPositionTooltipBody: t('disablePositionEditConnectedToClientShift')
    };
  }

  /* if shift has pay query, disable position edit */
  if (hasPayQuery) {
    return {
      isPositionEditDisabled: true,
      disabledPositionTooltipBody: hasPosition ? t('disablePositionEditPayQuery') : t('noPositionTooltipPayQueryExist')
    };
  }

  /* if shift has processed payroll, disable position edit */
  if (hasProcessedPayroll) {
    return {
      isPositionEditDisabled: true,
      disabledPositionTooltipBody: hasPosition ? t('disablePositionEditProcessed') : t('noPositionTooltipProcessed')
    };
  }

  /* if shift has approved timesheets, disable position edit */
  if (hasApprovedTimesheets) {
    return {
      isPositionEditDisabled: true,
      disabledPositionTooltipBody: hasPosition
        ? t('disablePositionEditApprovedTimesheets')
        : t('noPositionTooltipTimesheetApproved')
    };
  }

  return { isPositionEditDisabled: false, disabledPositionTooltipBody: null };
};

// Sync filter IDs with current configuration based on slug matching.
// Used for filter groups that generate runtime UUID ids in filter config.
const syncFilterIds = (filterArray, allFiltersArray) => {
  if (!Array.isArray(filterArray) || !Array.isArray(allFiltersArray)) {
    return filterArray;
  }

  return filterArray.map((filter) => {
    const matchingConfig = allFiltersArray.find((filterFromAll) => filterFromAll.slug === filter.slug);

    if (matchingConfig) {
      return {
        ...filter,
        id: matchingConfig.id
      };
    }

    return filter;
  });
};

/**
 * Prepares filters for rendering in the Filter component
 * Handles legacy nested structure and ensures proper format: { basic: [], fields: [] }
 */
export const convertFiltersForRendering = ({ filters, allFilters, selectedKey }) => {
  if (!filters || Array.isArray(filters)) {
    return filters;
  }

  const result = {};

  if (Array.isArray(filters.basic)) {
    result.basic = syncFilterIds(filters.basic, allFilters?.[selectedKey]?.basic);
  }

  if (Array.isArray(filters.fields)) {
    result.fields = syncFilterIds(filters.fields, allFilters?.[selectedKey]?.fields);
  }

  return result;
};

export const migrateBoardShiftFilters = ({ filters, mappedLocations }) =>
  migrateLegacyLocationsFilter({
    filters,
    legacySlug: PROJECT_SCHEDULING_FILTERS_SLUGS.locations,
    mainLocationsSlug: PROJECT_SCHEDULING_FILTERS_SLUGS.mainLocations,
    mappedLocations
  });

export * from './createNewMainEventEligibility';

export const isProjectLocationDisabled = ({ accountId, selectedProject, mappedClientBranches, billingStatus }) => {
  if (accountId === ONDI_ACCOUNT_ID) {
    return false;
  }

  if (selectedProject?.deletedOrderClientBranchId) {
    return true;
  }

  return !getCanAdjustShift({ project: selectedProject, mappedClientBranches, billingStatus });
};

export const isMainEventOverAWeekFromProjects = ({ projectIds = [], projectById = {}, mainStartDate, mainEndDate }) => {
  const selectedProjects = projectIds.map((id) => projectById[id]).filter(Boolean);
  if (!selectedProjects.length || !mainStartDate) return false;

  const mainStart = new Date(mainStartDate);
  const mainEnd = new Date(mainEndDate || mainStartDate);

  return selectedProjects.some((project) => {
    const projectStart = new Date(project.date);
    const projectEnd = new Date(project.endDate || project.date);
    const gapAfterProjectEnd = (mainStart - projectEnd) / MILLISECONDS_IN_A_DAY;
    const gapBeforeProjectStart = (projectStart - mainEnd) / MILLISECONDS_IN_A_DAY;
    return gapAfterProjectEnd > 7 || gapBeforeProjectStart > 7;
  });
};

// Budget only counts events with at least one shift on the day that shows on the board.
export const getBudgetProjectsOfDate = ({ projects, date, isProjectStatsActualBooked = false }) =>
  getFilteredProjectsShiftsByDate({ projects: projects || [], date }).filter(({ shifts }) =>
    (shifts || []).some((shift) => isShiftOnBudgetBoard({ shift, isProjectStatsActualBooked }))
  );

/* The calendar cell plus is an order entry point on client accounts: a host account with an activated
   supplier opens the create order modal, and a freemium client can only ever order - it never creates
   its own events, whether or not its suppliers are activated yet. The disable gate derives from the
   same predicate on purpose - the plus is gated on CREATE_ORDER exactly where it creates an order, so
   it can never be blocked for a missing order privilege while still opening the event creation modal. */
export const isOrderCellEntryPoint = ({ isHost, hasEngagedSuppliers, billingStatus }) =>
  (!!isHost && !!hasEngagedSuppliers) || billingStatus === ACCOUNT_BILLING_STATUS_MAP.CLIENT_FREEMIUM;

export const shouldDisableCalendarAddEntryPoint = ({ isHost, hasEngagedSuppliers, billingStatus, canCreateOrder }) =>
  !canCreateOrder && isOrderCellEntryPoint({ isHost, hasEngagedSuppliers, billingStatus });
